import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";
import {
  getWorkspaceSettings,
  updateWorkspaceSettings,
  WorkspaceSettings,
} from "../../../../helpers/workspace_settings_helper";
import { PLATFORM_TEMPLATE_GALLERY_TAGS } from "../../../../helpers/platform_templates_helper";
import { packIdFromGalleryTag } from "../../../../constants/suggestedContactFieldPacks";
import { isOwner } from "../../../../utils/tenantRoles";

const WorkspaceSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const owner = isOwner();
  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);
  const [loading, setLoading] = useState(owner);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!owner) return;
    setLoading(true);
    setError(null);
    try {
      setSettings((await getWorkspaceSettings()).data);
    } catch (e: any) {
      setError(e?.message || t("workspace.error.load"));
    } finally {
      setLoading(false);
    }
  }, [owner, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (key: keyof WorkspaceSettings) => {
    setSettings((current) =>
      current ? { ...current, [key]: !current[key] } : current
    );
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await updateWorkspaceSettings(settings);
      setSettings(res.data);
      toast.success(res.message || t("workspace.success"));
    } catch (e: any) {
      toast.error(e?.message || t("workspace.error.save"));
    } finally {
      setSaving(false);
    }
  };

  document.title = t("workspace.documentTitle");
  const helpHref = helpUrlForRoute("/settings/workspace", i18n.language);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("workspace.title")} actions={<PageHelpLink href={helpHref} label={t("common.help")} />} />
        <ToastContainer closeButton={false} limit={3} />
        {!owner ? <Alert color="warning">{t("permissions.ownerOnly")}</Alert> : loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : error || !settings ? (
          <Alert color="danger">{error || t("workspace.error.load")} <Button color="link" size="sm" onClick={() => void load()}>{t("common.retry")}</Button></Alert>
        ) : (
          <Row className="justify-content-center"><Col lg={8} xl={7}>
            <p className="text-muted">{t("workspace.subtitle")}</p>
            <Card>
              <CardHeader><h5 className="card-title mb-0">{t("workspace.cardTitle")}</h5></CardHeader>
              <CardBody>
                {([
                  ["send_approval_required", "workspace.approval"],
                  ["weekly_digest_enabled", "workspace.digest"],
                  ["include_web_version_link", "workspace.webVersion"],
                ] as const).map(([key, prefix]) => (
                  <div className="form-check form-switch mb-4" key={key}>
                    <Input id={key} className="form-check-input" type="switch" checked={Boolean(settings[key])} onChange={() => toggle(key)} />
                    <Label className="form-check-label fw-medium" htmlFor={key}>{t(`${prefix}.label`)}</Label>
                    <p className="text-muted small mb-0">{t(`${prefix}.help`)}</p>
                  </div>
                ))}
                <div className="mb-4">
                  <Label htmlFor="bounce_rate_warn_threshold_percent" className="fw-medium">
                    {t("workspace.bounceWarn.label")}
                  </Label>
                  <div className="d-flex align-items-center gap-2" style={{ maxWidth: 160 }}>
                    <Input
                      type="number"
                      id="bounce_rate_warn_threshold_percent"
                      min={1}
                      max={20}
                      step={1}
                      value={settings.bounce_rate_warn_threshold_percent ?? 5}
                      onChange={(e) => {
                        const raw = Number(e.target.value);
                        const next = Number.isFinite(raw)
                          ? Math.max(1, Math.min(20, Math.round(raw)))
                          : 5;
                        setSettings((current) =>
                          current
                            ? {
                                ...current,
                                bounce_rate_warn_threshold_percent: next,
                              }
                            : current
                        );
                      }}
                    />
                    <span className="text-muted">%</span>
                  </div>
                  <p className="text-muted small mb-0 mt-1">
                    {t("workspace.bounceWarn.help")}
                  </p>
                </div>
                <div className="mb-4">
                  <Label htmlFor="preferred_gallery_tag" className="fw-medium">
                    {t("workspace.galleryTag.label")}
                  </Label>
                  <Input
                    type="select"
                    id="preferred_gallery_tag"
                    value={settings.preferred_gallery_tag || ""}
                    onChange={(e) =>
                      setSettings((current) =>
                        current
                          ? {
                              ...current,
                              preferred_gallery_tag: e.target.value || null,
                            }
                          : current
                      )
                    }
                  >
                    <option value="">{t("workspace.galleryTag.none")}</option>
                    {PLATFORM_TEMPLATE_GALLERY_TAGS.map((tag) => (
                      <option key={tag} value={tag}>
                        {t(`platformTemplates.tag.${tag}`, { defaultValue: tag })}
                      </option>
                    ))}
                  </Input>
                  <p className="text-muted small mb-0 mt-1">
                    {t("workspace.galleryTag.help")}
                  </p>
                  {packIdFromGalleryTag(settings.preferred_gallery_tag) && (
                    <p className="small mb-0 mt-2">
                      <Link
                        to={`/settings/contact-fields?pack=${settings.preferred_gallery_tag}`}
                      >
                        {t("workspace.galleryTag.packLink")}
                      </Link>
                    </p>
                  )}
                </div>
                <Button color="primary" disabled={saving} onClick={() => void save()}>
                  {saving ? <Spinner size="sm" className="me-2" /> : null}{t("workspace.save")}
                </Button>
              </CardBody>
            </Card>
          </Col></Row>
        )}
      </Container>
    </div>
  );
};

export default WorkspaceSettingsPage;
